/**
 * Camera page utilities — small pure/browser-async helpers extracted from
 * Camera.jsx so multi-file, retry-queue, and bulk-processing code paths can
 * share them without living inside a React component file.
 */

/** Max items allowed in the bulk-file picker before we trim + toast. */
export const MAX_BULK = 12;

/**
 * Split a user-typed tag string (comma-separated) into a de-duplicated list
 * of trimmed, non-empty tags. Server-side sanitizer normalizes/rejects the
 * rest so we stay lenient here.
 */
export const parseTagList = (raw) => {
  const seen = new Set();
  const out = [];
  for (const p of String(raw || "").split(",")) {
    const t = p.trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

/** Only accept common web-safe image MIME types — decoder support is broad. */
export const isSupportedImage = (file) => /^image\/(jpe?g|png|webp)$/i.test(file.type);

/**
 * Request the user's current geolocation once. Resolves to `null` when
 * denied, unavailable, or timed out — never throws. Uses low-accuracy mode
 * (cell/wifi triangulation) to avoid GPS spin-up delays that would block the
 * capture flow.
 */
export const getLocationOnce = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  });
